import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AccessibilityInfo, AppState, Keyboard, Platform, TextInput, View, Text, useWindowDimensions } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useSegments } from 'expo-router';
import { supabase } from '@/data/client';
import { getLoggedInUserInfo } from '@/data/people';
import { getGuideState, recordGuideProgress, setAutoGuidesEnabled } from '@/data/guides';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { eligibleSteps, guideSurfaceIDs, featureKey, pendingSteps, resumeSteps, mergeProgress, guideTransitionTimer } from '@/utils/guides';
import { getGuide, GUIDES_ENABLED } from '@/config/guides';
import useGuideStore from '@/data/store/useGuideStore';
import useDropdownStore from '@/data/store/useDropdownStore';
import useLoader from '@/utils/hooks/useLoader';
import useConfirmationStore from '@/data/store/useConfirmationStore';
import GuideOverlay, { GuideOverlayEmbedded } from './GuideOverlay';

export const GuideContext = createContext(null);
export const GuidePageContext = createContext(null);
export const GuideProgressContext = createContext(null);
// Separate, narrow context for surfaces that can host tour steps (e.g. the
// filter sheet). Keeps the overlay element and host set out of GuideContext
// so GuideTarget consumers don't re-render on every target/measure update.
export const GuideHostedContext = createContext(null);
// Narrow read-only handshake for other fullscreen presenters (Phase E
// milestone celebrations). Exposes whether a tour is active plus a single
// reservation slot so a guide and a celebration can never both pass their
// idle checks in the same tick — guides keep priority while starting or
// active; a held reservation blocks new tours until released.
export const GuidePresentationContext = createContext(null);

export default function GuideProvider({ children, blocked = false }) {
    const [accountID, setAccountID] = useState(null);
    const [authOrganizationID, setAuthOrganizationID] = useState(null);
    const [page, setPage] = useState(null);
    const [active, setActive] = useState(null);
    const [target, setTarget] = useState(null);
    const [screenReader, setScreenReader] = useState(false);
    const [keyboard, setKeyboard] = useState(false);
    const [foreground, setForeground] = useState(AppState.currentState !== 'background');
    const [message, setMessage] = useState(null);
    const viewport = useWindowDimensions();
    const viewportRef = useRef(viewport);
    viewportRef.current = viewport;
    const inspectionOpened = useRef(false);
    const transitionTiming = useRef(null);
    const registry = useRef(new Map());
    const attempted = useRef(new Set());
    const generation = useRef(0);
    const writeVersion = useRef(0);
    const replayRequest = useRef(null);
    const accountRef = useRef(accountID);
    accountRef.current = accountID;
    const queryClient = useQueryClient();
    const router = useRouter();
    const segments = useSegments();
    const navigationRef = useRef({ router, segments });
    navigationRef.current = { router, segments };
    const { data: state, isError, refetch } = Query.guides.state(GUIDES_ENABLED ? accountID : null);
    const blockers = useGuideStore(s => s.blockers);
    const resetVersion = useGuideStore(s => s.resetVersion);
    const dropdownOpen = useDropdownStore(s => s.open);
    const loaderOpen = useLoader(s => s.isEnabled);
    const confirmationOpen = useConfirmationStore(s => s.isOpen);
    const cannotShow = blocked || keyboard || !foreground || loaderOpen || confirmationOpen || dropdownOpen || Object.keys(blockers).length > 0 || (!!page && authOrganizationID !== page.context.organizationID);
    const currentRef = useRef({ page, active, cannotShow, accountID });
    currentRef.current = { page, active, cannotShow, accountID };
    // Single-slot presentation reservation shared with other fullscreen
    // presenters (e.g. milestone celebrations). While reserved, begin()
    // refuses to start a tour; reserve() refuses while a tour is active.
    const [presentationReservation, setPresentationReservation] = useState(null);
    const presentationRef = useRef(null);
    presentationRef.current = presentationReservation;
    const reservePresentation = useCallback(id => {
        if (presentationRef.current || currentRef.current.active) return false;
        presentationRef.current = id;
        setPresentationReservation(id);
        return true;
    }, []);
    const releasePresentation = useCallback(id => {
        if (presentationRef.current !== id) return;
        presentationRef.current = null;
        setPresentationReservation(null);
    }, []);

    useEffect(() => {
        let live = true;
        const updateSession = session => {
            setAccountID(session?.user?.id || null);
            setAuthOrganizationID(session?.user?.app_metadata?.organizationID || null);
        };
        supabase.auth.getSession().then(({ data }) => { if (live) updateSession(data.session); });
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => updateSession(session));
        return () => { live = false; subscription.unsubscribe(); };
    }, []);

    useEffect(() => {
        generation.current += 1;
        setActive(null);
        setTarget(null);
        replayRequest.current = null;
        attempted.current.clear();
        // A reservation belongs to the old identity's presenter — drop it so
        // a crashed celebration can't hold the surface across sign-in.
        presentationRef.current = null;
        setPresentationReservation(null);
    }, [accountID, resetVersion]);

    useEffect(() => {
        if (Platform.OS !== 'web') AccessibilityInfo.isScreenReaderEnabled().then(setScreenReader);
        const reader = Platform.OS !== 'web' ? AccessibilityInfo.addEventListener('screenReaderChanged', setScreenReader) : null;
        const show = Keyboard.addListener('keyboardDidShow', () => setKeyboard(true));
        const hide = Keyboard.addListener('keyboardDidHide', () => setKeyboard(false));
        const app = AppState.addEventListener('change', next => {
            setForeground(next === 'active');
            if (next === 'active') refetch();
        });
        return () => { reader?.remove(); show.remove(); hide.remove(); app.remove(); };
    }, [refetch]);

    const registerPage = useCallback(next => setPage(next), []);
    const unregisterPage = useCallback(id => setPage(previous => previous?.id === id ? null : previous), []);
    const registerTarget = useCallback((id, instance, entry) => {
        if (!registry.current.has(id)) registry.current.set(id, new Map());
        registry.current.get(id).set(instance, entry);
        return () => {
            const entries = registry.current.get(id);
            if (entries?.get(instance) === entry) entries.delete(instance);
        };
    }, []);

    const measure = useCallback(async step => {
        const entries = registry.current.get(step.targetID);
        const entry = [...(entries?.values() || [])].find(item => item.focused && item.node);
        if (!entry) return step.fallback ? { fallback: true } : null;
        if (Platform.OS === 'web') {
            const rect = entry.node.getBoundingClientRect?.();
            if (!rect || !rect.width || !rect.height) return null;
            const offscreen = rect.top < 0 || rect.left < 0 || rect.bottom > window.innerHeight || rect.right > window.innerWidth;
            return { x: rect.x, y: rect.y, width: rect.width, height: rect.height, entry, offscreen };
        }
        return new Promise(resolve => {
            const timeout = setTimeout(() => resolve(null), 500);
            entry.node.measureInWindow?.((x, y, width, height) => {
                clearTimeout(timeout);
                const screen = viewportRef.current;
                resolve(width > 0 && height > 0 ? { x, y, width, height, entry, offscreen: y < 0 || x < 0 || y + height > screen.height || x + width > screen.width } : null);
            });
        });
    }, []);

    const begin = useCallback(async (manual, selectedPage, { resume = false } = {}) => {
        if (!GUIDES_ENABLED || !getGuide(selectedPage?.guideID) || !selectedPage?.ready || !accountRef.current || currentRef.current.cannotShow || currentRef.current.active || presentationRef.current) return;
        if (Platform.OS === 'web' && document.activeElement?.matches?.('input, textarea, [contenteditable="true"]')) return;
        if (Platform.OS !== 'web' && TextInput.State.currentlyFocusedInput()) return;
        const guide = getGuide(selectedPage.guideID);
        const stamp = ++generation.current;
        const account = accountRef.current;
        let saved = queryClient.getQueryData(QueryKeys.guides.state(account));
        if (!manual) {
            try {
                saved = await queryClient.fetchQuery({ queryKey: QueryKeys.guides.state(account), queryFn: () => getGuideState(account), staleTime: 30000 });
            } catch { return; }
            if (saved.autoGuidesEnabled === false) return;
        }
        let freshUser;
        try {
            freshUser = await queryClient.fetchQuery({ queryKey: QueryKeys.people.loggedInUser(), queryFn: getLoggedInUserInfo, staleTime: 15000 });
        } catch { return; }
        if (!freshUser?.id || freshUser.organizationID !== selectedPage.context.organizationID) return;
        const eligible = eligibleSteps(guide, { ...selectedPage.context, user: freshUser });
        // Hosted steps live inside a surface (sheet/modal) that an earlier
        // activate step opens. Pull that opener in when it's missing —
        // otherwise the tour would reach hosted steps with nothing open.
        const withOpeners = resumeSteps(eligible, manual && !resume ? [] : saved?.progress);
        const available = withOpeners.filter(step => {
            // The surface control stands in for hosted steps here — their
            // real targets only exist once the surface is open.
            const probe = step.surface ? { ...step, targetID: step.surface, fallback: true } : step;
            return probe.fallback || !!selectedPage.scrollToTarget || [...(registry.current.get(probe.targetID)?.values() || [])].some(entry => entry.focused && entry.node);
        });
        if (stamp !== generation.current || account !== accountRef.current || currentRef.current.page?.id !== selectedPage.id || currentRef.current.cannotShow || presentationRef.current) return;
        if (!available.length) {
            if (manual) setMessage('No guide steps are available on this page right now.');
            return;
        }
        setActive({ pageID: selectedPage.id, organizationID: selectedPage.context.organizationID, accountID: account, guideID: guide.id, steps: available, eligible: available, index: 0, manual, inspecting: false });
    }, [measure, queryClient]);

    useEffect(() => {
        if (active && (page?.id !== active.pageID || page?.context.organizationID !== active.organizationID || accountID !== active.accountID)) {
            generation.current += 1;
            setActive(null);
            setTarget(null);
        }
    }, [page, active, accountID]);

    useEffect(() => {
        if (!page?.ready || cannotShow || active || !accountID) return;
        const request = replayRequest.current;
        if (request?.guideID === page.guideID) {
            replayRequest.current = null;
            attempted.current.add(page.id);
            begin(true, page, request);
            return;
        }
        if (!state?.synced || isError || !state.autoGuidesEnabled || attempted.current.has(page.id)) return;
        const timer = setTimeout(() => {
            attempted.current.add(page.id);
            begin(false, page);
        }, 650);
        return () => clearTimeout(timer);
    }, [page, cannotShow, active, accountID, state, isError, begin]);

    useEffect(() => {
        if (!active) return;
        if (cannotShow && !active.inspecting) {
            const step = active.steps[active.index];
            // A blocker appearing during a hosted step is usually a surface
            // the user opened inside the host sheet — suspend like inspect
            // mode instead of ending the tour.
            if (step?.surface) {
                setActive(previous => previous ? { ...previous, inspecting: true } : null);
                return;
            }
            generation.current += 1;
            setActive(null);
            setTarget(null);
            return;
        }
        if (active.inspecting || !page?.ready) return;
        let live = true;
        let inflight = false;
        let failures = 0;
        const step = active.steps[active.index];
        const timing = transitionTiming.current?.featureID === step.featureID ? transitionTiming.current : null;
        const update = async () => {
            if (inflight) return;
            inflight = true;
            try {
                if (!page || !eligibleSteps(getGuide(active.guideID), page.context).some(item => featureKey(item) === featureKey(step))) {
                    if (live) { setActive(null); setTarget(null); }
                    return;
                }
                let measured = await measure(step);
                if (!measured && page.scrollToTarget) {
                    await page.scrollToTarget(step.targetID);
                    await new Promise(resolve => setTimeout(resolve, 180));
                    measured = await measure(step);
                }
                if (measured?.offscreen) {
                    await measured.entry.ensureVisible?.();
                    await new Promise(resolve => setTimeout(resolve, 180));
                    measured = await measure(step);
                }
                if (!live) return;
                // measureInWindow can transiently fail on native while sheets
                // or transitions are mid-flight — retry instead of dropping
                // the whole tour on a single missed frame.
                if (!measured || measured.offscreen) {
                    timing?.log(measured?.offscreen ? 'target offscreen' : 'target retry');
                    if (++failures >= 4) {
                        if (step.surface) {
                            // The host surface closed mid-tour (e.g. swipe
                            // dismiss) — skip its remaining steps rather than
                            // ending the whole tour.
                            const nextIndex = active.steps.findIndex((item, i) => i > active.index && item.surface !== step.surface);
                            setActive(previous => {
                                if (!previous || nextIndex === -1) return null;
                                return { ...previous, index: nextIndex, inspecting: false };
                            });
                        } else {
                            // An auto-started tour whose target can never be
                            // measured would flash then die on every visit —
                            // mark the step so it stops re-arming.
                            if (!active.manual) persist([{ featureID: step.featureID, learningRevision: step.learningRevision, status: 'skipped' }]);
                            setActive(null);
                        }
                        setTarget(null);
                    }
                    return;
                }
                failures = 0;
                if (timing && !timing.measured) { timing.measured = true; timing.log('target ready'); }
                setTarget(previous => measured.fallback ? previous?.fallback ? previous : measured : previous?.entry === measured.entry && ['x', 'y', 'width', 'height'].every(key => Math.abs(previous[key] - measured[key]) < 0.5) ? previous : measured);
            } finally {
                inflight = false;
            }
        };
        setTarget(null);
        update();
        const interval = setInterval(update, 250);
        return () => { live = false; clearInterval(interval); };
    }, [active, page, cannotShow, measure]);

    useEffect(() => {
        if (!active?.inspecting) return;
        if (blocked || !foreground || loaderOpen) {
            setActive(null);
            return;
        }
        if (active.waitingMode === 'open') {
            const timer = setTimeout(() => {
                setTarget(null);
                setActive(previous => previous?.waitingMode === 'open' ? { ...previous, index: previous.returnIndex, inspecting: false, waitingFor: null, waitingMode: null } : previous);
                setMessage('The control did not open. Tap the highlighted control to try again.');
            }, 2000);
            return () => clearTimeout(timer);
        }
        if (active.waitingMode === 'close') return;
        if (cannotShow || !page?.ready) {
            inspectionOpened.current = true;
            return;
        }
        if (inspectionOpened.current) {
            setActive(previous => previous ? { ...previous, inspecting: false } : null);
            return;
        }
        // The activated feature never opened a blocking surface (modal,
        // dropdown, …) — resume rather than leaving the tour hidden forever.
        const timer = setTimeout(() => setActive(previous => previous ? { ...previous, inspecting: false } : null), 650);
        return () => clearTimeout(timer);
    }, [cannotShow, active?.inspecting, active?.waitingMode, active?.waitingFor, blocked, foreground, loaderOpen, page?.ready]);

    useEffect(() => {
        if (!message) return;
        const timer = setTimeout(() => setMessage(null), 4000);
        return () => clearTimeout(timer);
    }, [message]);

    const persist = useCallback(async items => {
        const account = accountRef.current;
        if (!account || !items.length) return;
        const version = ++writeVersion.current;
        queryClient.setQueryData(QueryKeys.guides.state(account), previous => ({ ...previous, progress: mergeProgress(previous?.progress, items), synced: false }));
        try {
            const next = await recordGuideProgress(account, items);
            if (account === accountRef.current) queryClient.setQueryData(QueryKeys.guides.state(account), previous => version === writeVersion.current ? { ...next, progress: mergeProgress(next.progress, previous?.progress) } : { ...previous, progress: mergeProgress(next.progress, previous?.progress) });
        } catch { setMessage('Unable to save guide progress on this device.'); }
    }, [queryClient]);

    const onTargetActivated = useCallback(targetID => {
        const { active: tour, page: focused } = currentRef.current;
        const step = tour?.steps[tour.index];
        if (!step || tour.inspecting || focused?.id !== tour.pageID || accountRef.current !== tour.accountID || step.targetID !== targetID || step.interaction !== 'activate') return false;
        if (!step.eligible(focused.context)) { setActive(null); setTarget(null); return false; }
        persist([{ featureID: step.featureID, learningRevision: step.learningRevision, status: 'viewed' }]);
        inspectionOpened.current = false;
        const waitingFor = step.opensSurface || (step.closesSurface ? step.surface : null);
        const waitingMode = step.opensSurface ? tour.steps[tour.index + 1]?.surface === step.opensSurface ? 'open' : 'dismiss' : step.closesSurface ? 'close' : null;
        const upcoming = tour.index === tour.steps.length - 1 ? null : { ...tour, index: tour.index + 1, inspecting: !!waitingFor || ['dismiss', 'ready'].includes(step.waitFor), waitingFor, waitingMode, returnIndex: tour.index };
        currentRef.current.active = upcoming;
        setTarget(null);
        setActive(upcoming);
        return true;
    }, [persist]);
    const onSurfaceShown = useCallback(surfaceID => {
        const current = currentRef.current.active;
        const opener = getGuide(current?.guideID)?.surfaces?.[surfaceID]?.opener;
        if (current && opener && current.steps[current.index]?.featureID === opener) onTargetActivated(current.steps[current.index].targetID);
        const tour = currentRef.current.active;
        if (tour?.waitingMode !== 'open' || tour.waitingFor !== surfaceID || tour.steps[tour.index]?.surface !== surfaceID) return;
        setActive({ ...tour, inspecting: false, waitingFor: null, waitingMode: null });
    }, [onTargetActivated]);
    const onSurfaceAction = useCallback(surfaceID => {
        const { active: tour, page: focused } = currentRef.current;
        const step = tour?.steps[tour.index];
        if (!step || focused?.id !== tour.pageID || step.surface !== surfaceID || step.completion !== 'selection' || !step.eligible(focused.context)) return;
        persist([{ featureID: step.featureID, learningRevision: step.learningRevision, status: 'viewed' }]);
        setTarget(null);
        const upcoming = tour.index === tour.steps.length - 1 ? null : { ...tour, index: tour.index + 1, inspecting: true, waitingFor: surfaceID, waitingMode: 'close' };
        currentRef.current.active = upcoming;
        setActive(upcoming);
    }, [persist]);
    const onSurfaceClosed = useCallback(surfaceID => {
        const { active: tour, page: focused } = currentRef.current;
        if (!tour || focused?.id !== tour.pageID) return;
        if (tour.waitingFor === surfaceID && ['close', 'dismiss'].includes(tour.waitingMode)) {
            inspectionOpened.current = true;
            setActive({ ...tour, waitingFor: null, waitingMode: null });
            return;
        }
        const guide = getGuide(tour.guideID);
        const step = tour.steps[tour.index];
        if (!guideSurfaceIDs(guide, step.surface).includes(surfaceID)) return;
        const surface = guide?.surfaces?.[surfaceID];
        const opener = surface?.cancel === 'retry' ? tour.steps.findIndex(item => item.featureID === surface.opener) : -1;
        if (opener >= 0) {
            setTarget(null);
            setActive({ ...tour, index: opener, inspecting: false, waitingFor: null, waitingMode: null });
            return;
        }
        if (step.closesSurface && step.completion !== 'selection' && step.eligible(focused.context)) persist([{ featureID: step.featureID, learningRevision: step.learningRevision, status: 'viewed' }]);
        const index = tour.steps.findIndex((item, i) => i > tour.index && !guideSurfaceIDs(guide, item.surface).includes(surfaceID));
        inspectionOpened.current = true;
        setTarget(null);
        setActive(index === -1 ? null : { ...tour, index, inspecting: true, waitingFor: null, waitingMode: null });
    }, [persist]);

    const toggleAutomatic = useCallback(async enabled => {
        const account = accountRef.current;
        if (!account) return;
        const version = ++writeVersion.current;
        queryClient.setQueryData(QueryKeys.guides.state(account), previous => ({ ...previous, autoGuidesEnabled: enabled, synced: false }));
        try {
            const next = await setAutoGuidesEnabled(account, enabled);
            if (account === accountRef.current && version === writeVersion.current) queryClient.setQueryData(QueryKeys.guides.state(account), previous => ({ ...next, progress: mergeProgress(next.progress, previous?.progress) }));
        } catch { setMessage('Unable to save guide preferences on this device.'); }
    }, [queryClient]);

    const next = () => {
        if (!active || !page) return;
        const step = active.steps[active.index];
        if (!target && !step.fallback && !screenReader) return;
        if (!step.eligible(page.context)) { setActive(null); return; }
        const upcoming = active.steps[active.index + 1];
        const log = getGuide(active.guideID)?.diagnostics && upcoming ? guideTransitionTimer(upcoming.featureID) : null;
        transitionTiming.current = log ? { featureID: upcoming.featureID, log, measured: false } : null;
        log?.('next pressed');
        persist([{ featureID: step.featureID, learningRevision: step.learningRevision, status: 'viewed' }]);
        setTarget(null);
        setActive(active.index === active.steps.length - 1 ? null : { ...active, index: active.index + 1 });
    };
    const back = () => {
        if (!active || active.index <= 0) return;
        const guide = getGuide(active.guideID);
        const current = active.steps[active.index];
        const previous = active.steps[active.index - 1];
        const leaving = current.surface && !guideSurfaceIDs(guide, previous.surface).includes(current.surface);
        const closeEntry = leaving ? [...(registry.current.get(guide?.surfaces?.[current.surface]?.closeTargetID)?.values() || [])].find(entry => entry.focused && entry.activate) : null;
        if (leaving && !closeEntry) return;
        const opener = !leaving && previous.surface && previous.surface !== current.surface ? active.steps.findIndex(step => step.featureID === guide?.surfaces?.[previous.surface]?.opener) : -1;
        const upcoming = { ...active, index: opener >= 0 ? opener : active.index - 1, inspecting: !!closeEntry, waitingFor: closeEntry ? current.surface : null, waitingMode: closeEntry ? 'close' : null };
        inspectionOpened.current = false;
        setTarget(null);
        currentRef.current.active = upcoming;
        setActive(upcoming);
        closeEntry?.activate();
    };
    const skip = () => {
        if (!active || !page) return;
        // Mark every step in the guide, not just the ones eligible right now —
        // otherwise context-gated steps (native-only, empty list, no target
        // yet) stay pending and the auto tour re-arms on every revisit.
        const unhandled = pendingSteps(getGuide(active.guideID)?.steps || [], state?.progress);
        persist(unhandled.map(step => ({ featureID: step.featureID, learningRevision: step.learningRevision, status: 'skipped' })));
        setActive(null);
        setTarget(null);
    };
    const replay = useCallback((guideID, { resume = false } = {}) => {
        const guide = getGuide(guideID);
        if (!guide || !GUIDES_ENABLED) return;
        const focused = currentRef.current.page;
        if (focused?.guideID === guideID && focused.ready && !currentRef.current.cannotShow) {
            attempted.current.add(focused.id);
            begin(true, focused, { resume });
        } else {
            // Queued either because a blocking surface (e.g. Dashboard Settings)
            // is still open — the registration effect fires once it clears — or
            // because the target page still needs a navigation push.
            replayRequest.current = { guideID, resume };
            if (focused?.guideID !== guideID) {
                const { router: currentRouter, segments: currentSegments } = navigationRef.current;
                const tab = currentSegments[0] === '(tabs)' ? currentSegments[1] : '(home)';
                currentRouter.push(`/(tabs)/${tab}${guide.route}`);
            }
        }
    }, [begin]);

    // Interactive steps: tapping the spotlighted control both performs the
    // feature's real action and advances the tour. The tour suspends while
    // the opened surface (sheet, dropdown) is up and resumes on the next step.
    const activate = () => {
        const entry = target?.entry;
        const step = active?.steps[active.index];
        if (entry?.activate && onTargetActivated(step?.targetID)) entry.activate();
    };
    // Surfaces the active tour still plans to visit — hosts like the filter
    // sheet suppress their own blocker while listed here so the overlay can
    // render above them.
    const hostedSurfaces = useMemo(() => {
        if (!active) return null;
        const surfaces = new Set();
        active.steps.slice(active.index).forEach(item => guideSurfaceIDs(getGuide(active.guideID), item.surface).forEach(id => surfaces.add(id)));
        return surfaces;
    }, [active]);
    const step = active?.steps[active.index];
    const stepAllowed = !!step && !!page && eligibleSteps(getGuide(active.guideID), page.context).some(item => featureKey(item) === featureKey(step));
    const measuring = !target && !screenReader;
    const handlers = useRef(null);
    handlers.current = { next, back, skip, activate };
    const hasActivate = step?.interaction === 'activate' && !!target?.entry?.activate;
    // Native modals presented on top of a sheet must mount inside that sheet's
    // tree (see SheetSelector's info-modal comment) — a root-level sibling
    // Modal lands underneath the pageSheet. The hosting surface renders the
    // overlay as a plain view inside its own tree (no nested Modal at all), so
    // highlight geometry shares the sheet's coordinate space. Web keeps the
    // root portal since its z-index already beats RNW modals.
    const hosted = Platform.OS !== 'web' && !!step?.surface;
    const OverlayComponent = hosted ? GuideOverlayEmbedded : GuideOverlay;
    const overlay = useMemo(() => stepAllowed && page.ready && !active.inspecting && !cannotShow
        ? <OverlayComponent embedded={hosted} measuring={measuring} step={step} index={active.index} total={active.steps.length} target={target?.fallback ? null : target} viewport={viewport} screenReader={screenReader} onTransitionPhase={transitionTiming.current?.featureID === step.featureID ? transitionTiming.current.log : undefined} onNext={() => handlers.current.next()} onBack={() => handlers.current.back()} onSkip={() => handlers.current.skip()} onActivate={hasActivate ? () => handlers.current.activate() : null} />
        : null,
    [stepAllowed, page?.ready, active?.inspecting, cannotShow, measuring, step, active?.index, active?.steps?.length, target, viewport, screenReader, hasActivate, hosted, OverlayComponent]);
    const hostedValue = useMemo(() => ({ hostedSurfaces, surfaceID: step?.surface, overlay: hosted ? overlay : null, onSurfaceShown, onSurfaceClosed, onSurfaceAction }), [hostedSurfaces, step?.surface, hosted, overlay, onSurfaceShown, onSurfaceClosed, onSurfaceAction]);
    const progressValue = useMemo(() => ({ state, isError }), [state, isError]);
    const value = useMemo(() => ({ accountID, registerPage, unregisterPage, registerTarget, onTargetActivated, replay, toggleAutomatic, resetVersion }), [accountID, registerPage, unregisterPage, registerTarget, onTargetActivated, replay, toggleAutomatic, resetVersion]);
    const presentationValue = useMemo(() => ({ active: !!active, reservedBy: presentationReservation, reserve: reservePresentation, release: releasePresentation }), [active, presentationReservation, reservePresentation, releasePresentation]);
    return <GuideContext.Provider value={value}>
        <GuideHostedContext.Provider value={hostedValue}>
            <GuideProgressContext.Provider value={progressValue}><GuidePageContext.Provider value={page}><GuidePresentationContext.Provider value={presentationValue}>{children}</GuidePresentationContext.Provider></GuidePageContext.Provider></GuideProgressContext.Provider>
        </GuideHostedContext.Provider>
        {hosted ? null : overlay}
        {!!message && <View pointerEvents="none" style={{ position: 'absolute', bottom: 100, left: 16, right: 16 }} accessibilityLiveRegion="polite"><Text>{message}</Text></View>}
    </GuideContext.Provider>;
}
